// wcwb/frontend/src/components/APBPanel.js
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { S } from './styles';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

function timeAgo(date) {
  const mins = Math.floor((Date.now() - new Date(date)) / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs/24)}d ago`;
}

export default function APBPanel({ event, isHost, currentUserId }) {
  const { token } = useAuth();
  const [apbs, setApbs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ item: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => { fetchAPBs(); }, [event.id]);

  // Poll every 15 seconds for new APBs
  useEffect(() => {
    const interval = setInterval(fetchAPBs, 15000);
    return () => clearInterval(interval);
  }, [event.id]);

  async function fetchAPBs() {
    try {
      const res = await fetch(`${API}/apb?event_id=${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setApbs(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function raiseAPB() {
    if (!form.item.trim()) return;
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API}/apb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ event_id: event.id, item: form.item, message: form.message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setApbs(prev => [data, ...prev]);
      setForm({ item: '', message: '' });
      setShowForm(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function claimAPB(apbId) {
    try {
      const res = await fetch(`${API}/apb/${apbId}/claim`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setApbs(prev => prev.map(a => a.id === apbId ? data : a));
    } catch (e) {
      alert(e.message);
    }
  }

  async function cancelAPB(apbId) {
    try {
      const res = await fetch(`${API}/apb/${apbId}/cancel`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setApbs(prev => prev.map(a => a.id === apbId ? data : a));
    } catch (e) {
      alert(e.message);
    }
  }

  const openAPBs      = apbs.filter(a => a.status === 'open');
  const resolvedAPBs  = apbs.filter(a => a.status !== 'open');

  return (
    <div>
      {/* Host: raise APB button */}
      {isHost && (
        <div style={{ marginBottom: 20 }}>
          {!showForm ? (
            <button
              style={{
                ...S.btnPrimary,
                background: '#B71C1C',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
              }}
              onClick={() => setShowForm(true)}>
              🚨 Raise an APB
            </button>
          ) : (
            <div style={{ background: '#FFF5F5', border: '2px solid #B71C1C', borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 20 }}>🚨</span>
                <h4 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 20, color: '#B71C1C' }}>
                  Raise an APB
                </h4>
              </div>
              {error && (
                <div style={{ background: '#FFEBEE', color: '#B71C1C', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>
              )}
              <label style={S.label}>What do you urgently need?</label>
              <input
                style={{ ...S.input, borderColor: '#B71C1C' }}
                placeholder="e.g. fresh basil, unsalted butter, red wine…"
                value={form.item}
                onChange={e => setForm(f => ({ ...f, item: e.target.value }))}
                autoFocus
              />
              <label style={S.label}>Extra message (optional)</label>
              <input
                style={S.input}
                placeholder="e.g. Any supermarket is fine, I need about 200g"
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.btnGhost} onClick={() => { setShowForm(false); setError(''); }}>Cancel</button>
                <button
                  style={{ ...S.btnPrimary, background: '#B71C1C', opacity: (!form.item || submitting) ? 0.5 : 1 }}
                  disabled={!form.item || submitting}
                  onClick={raiseAPB}>
                  {submitting ? 'Sending…' : '🚨 Send APB to all guests'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Open APBs */}
      {loading && <p style={{ color: '#7A6555', fontSize: 14 }}>Loading…</p>}

      {openAPBs.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {openAPBs.map(apb => (
            <div key={apb.id} style={{
              background: '#FFF5F5',
              border: '2px solid #B71C1C',
              borderRadius: 14,
              padding: 20,
              marginBottom: 12,
              animation: 'pulse 2s infinite',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 28, flexShrink: 0 }}>🚨</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 20, color: '#B71C1C', fontWeight: 600 }}>
                      APB — {apb.item}
                    </span>
                    <span style={{ fontSize: 11, color: '#7A6555' }}>{timeAgo(apb.created_at)}</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#5A3E2B', marginBottom: 4 }}>
                    <strong>{apb.profiles?.full_name}</strong> urgently needs this ingredient
                  </p>
                  {apb.message && (
                    <p style={{ fontSize: 13, color: '#7A6555', fontStyle: 'italic', marginBottom: 12 }}>
                      "{apb.message}"
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!isHost && apb.host_id !== currentUserId && (
                      <button
                        style={{ ...S.btnPrimary, background: '#2E7D32', fontSize: 13, padding: '8px 16px' }}
                        onClick={() => claimAPB(apb.id)}>
                        ✋ I'll bring this!
                      </button>
                    )}
                    {isHost && (
                      <button
                        style={{ background: 'none', border: '1px solid #B71C1C', color: '#B71C1C', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
                        onClick={() => cancelAPB(apb.id)}>
                        Cancel APB
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resolved APBs */}
      {resolvedAPBs.length > 0 && (
        <div>
          <p style={{ fontSize: 11, color: '#7A6555', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>Resolved</p>
          {resolvedAPBs.map(apb => (
            <div key={apb.id} style={{
              background: apb.status === 'claimed' ? '#F1F8E9' : '#FAFAFA',
              border: `1px solid ${apb.status === 'claimed' ? '#C5E1A5' : '#E5D9C8'}`,
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 8,
              opacity: 0.8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>{apb.status === 'claimed' ? '✅' : '❌'}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 500, fontSize: 14 }}>{apb.item}</p>
                  {apb.status === 'claimed' && apb.claimed_profile && (
                    <p style={{ fontSize: 12, color: '#2E7D32' }}>
                      {apb.claimed_profile.full_name} is bringing this · {timeAgo(apb.claimed_at)}
                    </p>
                  )}
                  {apb.status === 'cancelled' && (
                    <p style={{ fontSize: 12, color: '#7A6555' }}>Cancelled by host</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && apbs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🟢</div>
          <p style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 20, marginBottom: 8 }}>All good!</p>
          <p style={{ color: '#7A6555', fontSize: 13 }}>
            {isHost ? 'Use the APB button if you urgently need a guest to pick something up.' : 'No urgent requests from the host right now.'}
          </p>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(183,28,28,0.2); }
          50% { box-shadow: 0 0 0 8px rgba(183,28,28,0); }
        }
      `}</style>
    </div>
  );
}
